/**
 * View Mode Toggle Component
 * Phase 7: Toggle between Internal and Auditor views
 *
 * Per Evans' feedback: Auditors should only see approved, signed-off documents
 * Internal view shows QMS Health, working notes, drafts, and internal metrics
 */

import { Eye, EyeOff, Shield } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Badge } from '@/components/ui/badge'
import { useViewMode } from '@/contexts/ViewModeContext'

export function ViewModeToggle() {
  const { viewMode, setViewMode, isAuditorView } = useViewMode()

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant={isAuditorView ? 'default' : 'outline'}
          size="sm"
          className={`gap-2 ${isAuditorView ? 'bg-blue-600 hover:bg-blue-700' : ''}`}
        >
          {isAuditorView ? (
            <>
              <Shield className="h-4 w-4" />
              Auditor View
            </>
          ) : (
            <>
              <Eye className="h-4 w-4" />
              Internal View
            </>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel>View Mode</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={() => setViewMode('internal')}
          className="flex items-start gap-3 py-3"
        >
          <Eye className="h-4 w-4 mt-0.5" />
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <span className="font-medium">Internal View</span>
              {viewMode === 'internal' && (
                <Badge variant="secondary" className="text-xs">Active</Badge>
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Full access including QMS Health, drafts, and internal metrics
            </p>
          </div>
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => setViewMode('auditor')}
          className="flex items-start gap-3 py-3"
        >
          <Shield className="h-4 w-4 mt-0.5" />
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <span className="font-medium">Auditor View</span>
              {viewMode === 'auditor' && (
                <Badge variant="secondary" className="text-xs">Active</Badge>
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Shows only approved documents and compliance evidence
            </p>
          </div>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <div className="px-2 py-2 text-xs text-muted-foreground">
          {isAuditorView ? (
            <span className="flex items-center gap-1">
              <EyeOff className="h-3 w-3" />
              QMS Health and internal notes are hidden
            </span>
          ) : (
            <span className="flex items-center gap-1">
              <Eye className="h-3 w-3" />
              All information visible
            </span>
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

/**
 * Simple inline badge indicator for the view mode
 * Can be used in areas where space is limited
 */
export function ViewModeBadge() {
  const { isAuditorView } = useViewMode()

  if (!isAuditorView) return null

  return (
    <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
      <Shield className="h-3 w-3 mr-1" />
      Auditor View
    </Badge>
  )
}
